import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { appendAttributeIdToCategory } from "@/lib/catalog/categoryAttributeLinksStore";
import { codeExists, createCatalogAttribute, syncCatalogAttributesFromApi } from "@/lib/catalog/catalogAttributesStore";
import {
  listAttributeGroupRecords,
  syncAttributeGroupsFromApi,
  type AttributeGroupRecord,
} from "@/lib/catalog/attributeGroupsStore";
import type { Attribute, AttributeType, ID } from "@/lib/catalog/types";
import { slugify } from "@/lib/slugify";
import { isAxiosError } from "axios";
import { Loader2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const ATTRIBUTE_TYPES: AttributeType[] = [
  "text",
  "textarea",
  "number",
  "boolean",
  "date",
  "select",
  "multiselect",
  "image",
  "images",
  "file",
  "files",
];

type OptionRow = { id: ID; label: string; value: string };

export type QuickAddAttributeGroup = {
  id: ID;
  name: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefills the group select when opening; user can still change it. */
  group?: QuickAddAttributeGroup | null;
  categoryId: ID | null;
  onLinked: () => void | Promise<void>;
};

function formatApiErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.data) {
    const d = error.response.data as { message?: string; errors?: Record<string, string[]> };
    if (typeof d.message === "string" && d.message.trim()) return d.message;
    if (d.errors) {
      const first = Object.values(d.errors).flat().find((m) => m?.length);
      if (first) return first;
    }
  }
  return "Something went wrong. Please try again.";
}

/** Prefer Technical Specs when opening from Additional Information (no explicit group). */
function isTechnicalSpecsGroup(g: { id: ID; name: string; slug?: string }): boolean {
  const slug = (g.slug ?? "").toLowerCase().replace(/_/g, "-");
  const name = (g.name ?? "").toLowerCase().replace(/\s+/g, " ").trim();
  if (slug === "technical-specs" || slug === "technical-spec") return true;
  return name === "technical specs" || name.includes("technical spec");
}

function resolveDefaultGroupId(
  groups: AttributeGroupRecord[],
  preferred?: QuickAddAttributeGroup | null,
): string {
  if (preferred?.id && groups.some((g) => g.id === preferred.id)) return preferred.id;
  const technical = groups.find(isTechnicalSpecsGroup);
  if (technical) return technical.id;
  return groups[0]?.id ?? "";
}

export function QuickAddAttributeDialog({ open, onOpenChange, group = null, categoryId, onLinked }: Props) {
  const [groups, setGroups] = useState<AttributeGroupRecord[]>(() => listAttributeGroupRecords());
  const [groupId, setGroupId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<AttributeType>("boolean");
  const [isRequired, setIsRequired] = useState(false);
  const [isListing, setIsListing] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isInvoice, setIsInvoice] = useState(false);
  const [isDeliveryChecklist, setIsDeliveryChecklist] = useState(false);
  const [isGatePass, setIsGatePass] = useState(false);
  const [sortOrder, setSortOrder] = useState("0");
  const [accept, setAccept] = useState("");
  const [maxSizeMb, setMaxSizeMb] = useState("");
  const [maxFiles, setMaxFiles] = useState("");
  const [optionRows, setOptionRows] = useState<OptionRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setCode("");
    setName("");
    setType("boolean");
    setIsRequired(false);
    setIsListing(false);
    setIsFilter(false);
    setIsInvoice(false);
    setIsDeliveryChecklist(false);
    setIsGatePass(false);
    setSortOrder("0");
    setAccept("");
    setMaxSizeMb("");
    setMaxFiles("");
    setOptionRows([]);
    setInfoMessage(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    const cached = listAttributeGroupRecords();
    setGroups(cached);
    setGroupId(resolveDefaultGroupId(cached, group));
    void syncAttributeGroupsFromApi()
      .then(() => {
        const next = listAttributeGroupRecords();
        setGroups(next);
        setGroupId(resolveDefaultGroupId(next, group));
      })
      .catch(() => {
        /* keep cached groups */
      });
  }, [open, reset, group]);

  const needsOptions = type === "select" || type === "multiselect";
  const needsFileMeta = type === "file" || type === "files" || type === "image" || type === "images";

  const handleNameChange = (value: string) => {
    setName(value);
    setCode(slugify(value));
  };

  const addOptionRow = () => {
    setOptionRows((r) => [...r, { id: crypto.randomUUID(), label: "", value: "" }]);
  };

  const selectedGroup = groups.find((g) => g.id === groupId) ?? (group?.id === groupId ? group : null);

  const save = async () => {
    if (isSaving) return;
    if (!categoryId) {
      toast.error("No category is selected. Choose a category in basic information first.");
      return;
    }
    if (!groupId) {
      toast.error("Select an attribute group.");
      return;
    }
    if (!name.trim()) {
      toast.error("Attribute label is required.");
      return;
    }
    const effectiveCode = code.trim() || slugify(name);
    if (!effectiveCode) {
      toast.error("Attribute code is required (or use a name that can be slugified).");
      return;
    }
    const slugCode = slugify(effectiveCode);
    if (codeExists(slugCode)) {
      setInfoMessage("Another attribute already uses this code. Use a different label or code.");
      return;
    }
    setInfoMessage(null);

    const options: Attribute["options"] | undefined =
      needsOptions && optionRows.length
        ? optionRows
            .filter((r) => r.label.trim())
            .map((r, idx) => ({
              id: r.id,
              label: r.label.trim(),
              value: slugify(r.label) || `opt-${idx}`,
              sortOrder: idx,
            }))
        : undefined;

    if (needsOptions && (!options || options.length === 0)) {
      toast.error("Add at least one option for this field type.");
      return;
    }

    const payload = {
      attributeGroupId: groupId,
      code: slugCode,
      name: name.trim(),
      type,
      isRequired,
      isListing,
      isFilter,
      isInvoice,
      isDeliveryChecklist,
      isGatePass,
      sortOrder: Number(sortOrder) || 0,
      options,
      accept: needsFileMeta && accept.trim() ? accept.trim() : undefined,
      maxSizeMb: needsFileMeta && maxSizeMb.trim() ? Number(maxSizeMb) : undefined,
      maxFiles: type === "images" || type === "files" ? (maxFiles.trim() ? Number(maxFiles) : undefined) : undefined,
    };

    setIsSaving(true);
    try {
      const { record } = await createCatalogAttribute(payload);
      try {
        await appendAttributeIdToCategory(categoryId, record.id);
      } catch (linkError) {
        if (isAxiosError(linkError) && linkError.response?.status === 403) {
          toast.error(
            "The attribute was created, but you don’t have permission to link it to the category. Ask an admin, or add the link in Categories.",
          );
          try {
            await syncCatalogAttributesFromApi();
          } catch {
            /* best-effort */
          }
          await onLinked();
          reset();
          onOpenChange(false);
          return;
        }
        throw linkError;
      }
      try {
        await syncCatalogAttributesFromApi();
      } catch {
        /* best-effort; schema still refetched in parent */
      }
      await onLinked();
      reset();
      onOpenChange(false);
      const groupLabel = selectedGroup?.name ?? "the selected group";
      toast.success(`“${record.name}” is linked to this category and will appear in ${groupLabel}.`);
    } catch (e) {
      setInfoMessage(formatApiErrorMessage(e));
      toast.error(formatApiErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New attribute</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          Created in the selected group and linked to this product&apos;s category. No page refresh needed.
        </p>
        <div className="space-y-2 py-1">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Group *</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="glass-input h-10">
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Label *</Label>
              <Input
                className="glass-input h-10"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Code *</Label>
              <Input
                className="glass-input h-10 font-mono text-sm bg-muted/30"
                value={code}
                readOnly
                placeholder="auto-generated"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Sort order</Label>
              <Input
                className="glass-input h-10"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as AttributeType)}>
                <SelectTrigger className="glass-input h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATTRIBUTE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Required</span>
              <Checkbox checked={isRequired} onCheckedChange={(c) => setIsRequired(Boolean(c))} />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Show in listing</span>
              <Checkbox checked={isListing} onCheckedChange={(c) => setIsListing(Boolean(c))} />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Show in filters</span>
              <Checkbox checked={isFilter} onCheckedChange={(c) => setIsFilter(Boolean(c))} />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Show on invoice</span>
              <Checkbox checked={isInvoice} onCheckedChange={(c) => setIsInvoice(Boolean(c))} />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Show on delivery checklist</span>
              <Checkbox checked={isDeliveryChecklist} onCheckedChange={(c) => setIsDeliveryChecklist(Boolean(c))} />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-1.5 text-sm text-muted-foreground cursor-pointer">
              <span>Show on Gate Pass</span>
              <Checkbox checked={isGatePass} onCheckedChange={(c) => setIsGatePass(Boolean(c))} />
            </label>
          </div>

          {needsOptions ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Options</Label>
                <Button type="button" size="sm" variant="glass" className="h-7 text-xs" onClick={addOptionRow}>
                  + Option
                </Button>
              </div>
              <div className="space-y-2 rounded-lg border border-border/50 p-2 bg-muted/10">
                {optionRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 text-center">No options yet</p>
                ) : (
                  optionRows.map((row, idx) => (
                    <div key={row.id} className="flex gap-2 items-center">
                      <Input
                        className="glass-input h-9 text-sm"
                        placeholder="Label"
                        value={row.label}
                        onChange={(e) => {
                          const label = e.target.value;
                          const next = [...optionRows];
                          next[idx] = { ...row, label, value: slugify(label) };
                          setOptionRows(next);
                        }}
                      />
                      <Input
                        className="glass-input h-9 bg-muted/30 text-sm font-mono"
                        placeholder="auto-generated"
                        value={row.value}
                        readOnly
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-9 w-9 shrink-0 text-destructive"
                        onClick={() => setOptionRows(optionRows.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : null}

          {needsFileMeta ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Accept (MIME pattern)</Label>
                <Input
                  className="glass-input h-10 text-sm"
                  value={accept}
                  onChange={(e) => setAccept(e.target.value)}
                  placeholder="image/* or application/pdf"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Max size (MB)</Label>
                  <Input
                    className="glass-input h-10"
                    value={maxSizeMb}
                    onChange={(e) => setMaxSizeMb(e.target.value)}
                    type="number"
                  />
                </div>
                {(type === "images" || type === "files") && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Max files</Label>
                    <Input
                      className="glass-input h-10"
                      value={maxFiles}
                      onChange={(e) => setMaxFiles(e.target.value)}
                      type="number"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {infoMessage ? <p className="text-xs text-destructive">{infoMessage}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="glass" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="premium" onClick={() => void save()} disabled={isSaving || !categoryId || !groupId}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              "Save & link to category"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
